import { Check } from 'lucide-react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './day-picker-menu.module.scss';

export type DayPickerMenuEntry = {
  dayIndex: number;
  city: string | null;
  dateLabel: string;
  /** Number of open gaps on the day; 0 means the day is complete. */
  gaps: number;
};

const styles = {
  root: pickCss(css, css.root),
  header: pickCss(css, css.header),
  headerTitle: pickCss(css, css.headerTitle),
  headerMeta: pickCss(css, css.headerMeta),
  row: pickCss(css, css.row),
  num: pickCss(css, css.num),
  rowBody: pickCss(css, css.rowBody),
  rowTitle: pickCss(css, css.rowTitle),
  rowDate: pickCss(css, css.rowDate),
  pill: pickCss(css, css.pill),
};

type DayPickerMenuProps = {
  days: ReadonlyArray<DayPickerMenuEntry>;
  activeDayIndex: number;
  onSelect: (dayIndex: number) => void;
};

/**
 * DayPickerMenu — pen component `S9UTGZ`. The "Jump to day" list opened from
 * the `DayPickerBar`. Each row shows the day, city and date plus a status
 * pill: green "Done" or amber "N gaps".
 */
export const DayPickerMenu = ({
  days,
  activeDayIndex,
  onSelect,
}: DayPickerMenuProps) => {
  const attention = days.filter((d) => d.gaps > 0).length;
  return (
    <div className={styles.root()} role="menu">
      <div className={styles.header()}>
        <span className={styles.headerTitle()}>Jump to day</span>
        <span className={styles.headerMeta()}>
          {attention === 0
            ? 'All days complete'
            : `${attention} ${attention === 1 ? 'day needs' : 'days need'} attention`}
        </span>
      </div>
      {days.map((day) => {
        const isActive = day.dayIndex === activeDayIndex;
        return (
          <button
            className={joinCss(styles.row({ isActive }))}
            key={day.dayIndex}
            onClick={() => onSelect(day.dayIndex)}
            role="menuitem"
            type="button"
          >
            <span className={joinCss(styles.num({ isActive }))}>
              {day.dayIndex + 1}
            </span>
            <span className={styles.rowBody()}>
              <span className={styles.rowTitle()}>
                Day {day.dayIndex + 1}
                {day.city ? ` · ${day.city}` : ''}
              </span>
              <span className={styles.rowDate()}>{day.dateLabel}</span>
            </span>
            {day.gaps > 0 ? (
              <span className={joinCss(styles.pill({ tone: 'attention' }))}>
                {day.gaps} {day.gaps === 1 ? 'gap' : 'gaps'}
              </span>
            ) : (
              <span className={joinCss(styles.pill({ tone: 'done' }))}>
                <Check aria-hidden="true" size={10} />
                Done
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
